import React, { useCallback, useEffect, useState } from "react";
import { getSystemHealth } from "../../../api/globalAdminApi";
import { PageHeader, Panel, StatePanel, StatTiles, StatusChip } from "../ui/AdminUi";
import { I } from "../ui/icons";
import AhaHealthPanel from "./AhaHealthPanel";

/** Maps the backend's service status to the console's one status treatment. Missing status means unknown. */
export function serviceStatusOf(status) {
  if (!status) return { chip: "Unknown", ok: false, unknown: true };
  const s = String(status).toLowerCase();
  if (s === "ok") return { chip: "OK", ok: true };
  if (s === "degraded" || s === "warning" || s === "warn") return { chip: s === "degraded" ? "Degraded" : "Warn", ok: false };
  return { chip: "Down", ok: false };
}

const WORST_TONE = { Down: "bad", Unknown: "muted", Degraded: "warn" };

/**
 * Whether the platform is up, and where it is struggling. Problems come first, then the healthy services, then the
 * centres and recent issues. Only data the health endpoint returns is shown.
 */
export default function SystemHealthView() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(() => {
    setLoading(true);
    setError(null);
    getSystemHealth()
      .then((d) => setHealth(d))
      .catch((err) => setError(err?.message || "System health could not be checked."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  if (loading && !health) {
    return (
      <>
        <PageHeader title="System health" desc="Whether the platform is up, and where it is struggling. Checks run every 60 seconds." />
        <Panel><StatePanel kind="loading" what="health checks" /></Panel>
      </>
    );
  }
  if (error && !health) {
    return (
      <>
        <PageHeader title="System health" desc="Whether the platform is up, and where it is struggling. Checks run every 60 seconds." />
        <Panel><StatePanel kind="error" what="health checks" error={error} onRetry={refresh} /></Panel>
      </>
    );
  }

  const services = health?.services || [];
  const attention = services.filter((s) => !serviceStatusOf(s.status).ok);
  const healthy = services.filter((s) => serviceStatusOf(s.status).ok);
  const worst = attention.some((s) => serviceStatusOf(s.status).chip === "Down") ? "Down"
    : attention.some((s) => serviceStatusOf(s.status).unknown) ? "Unknown" : attention.length ? "Degraded" : "OK";
  const m = health?.metrics || {};

  return (
    <>
      <PageHeader
        title="System health"
        desc={`Whether the platform is up, and where it is struggling. Last checked ${health?.lastChecked || "—"}.`}
        actions={<button type="button" className="b sec" onClick={refresh} disabled={loading}><I.refresh s={14} />{loading ? "Checking…" : "Refresh"}</button>}
      />

      <Panel style={{ marginBottom: 16, borderColor: worst === "OK" ? undefined : `var(--st-${WORST_TONE[worst]})` }}>
        <div className="panel-b" style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
          <StatusChip status={worst} label={worst === "Down" ? "Partial outage" : worst} />
          <b style={{ fontSize: 16, fontWeight: 600 }}>
            {worst === "OK" ? `All ${services.length} services are operating normally.` : `${attention.length} of ${services.length} services need attention.`}
          </b>
          {worst !== "OK" && <span className="sub">Students and instructors may see delayed eCards and certificate emails.</span>}
        </div>
      </Panel>

      <StatTiles items={[
        { label: "Database connections", value: `${m.dbConnections?.active ?? "—"} / ${m.dbConnections?.max ?? "—"}`, foot: "active / max pool" },
        { label: "API uptime · 30 days", value: m.apiUptime?.value ?? "—", foot: "since last restart" },
        { label: "Error rate · 1 hour", value: `${m.errorRate?.value ?? "—"}%`, foot: m.errorRate?.label || "admin events", tone: Number(m.errorRate?.value) > 1 ? "var(--st-warn)" : undefined },
        { label: "JVM memory", value: `${m.jvmMemory?.used ?? "—"} MB`, foot: `of ${m.jvmMemory?.total ?? "—"} MB total` },
      ]} />

      {attention.length > 0 && (
        <>
          <div className="eyebrow" style={{ margin: "6px 0 10px" }}>Needs attention · {attention.length}</div>
          <div style={{ display: "grid", gap: 10, marginBottom: 22 }}>
            {attention.map((s) => {
              const st = serviceStatusOf(s.status);
              return (
                <Panel key={s.name} style={{ borderStyle: st.unknown ? "dashed" : "solid" }}>
                  <div className="panel-b" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(260px,100%),1fr))", gap: 16, alignItems: "start" }}>
                    <div>
                      <b style={{ fontWeight: 600 }}>{s.name}</b>
                      <div style={{ marginTop: 6 }}><StatusChip status={st.chip} /></div>
                    </div>
                    <div style={{ lineHeight: 1.55 }}>
                      {st.unknown ? "No heartbeat was received, so the monitor cannot confirm this service. It may be fine." : s.detail || "No detail reported."}
                      {s.metric && <div className="sub" style={{ marginTop: 4, fontSize: 12.5 }}>{s.metric} {s.metricLabel || ""}</div>}
                    </div>
                  </div>
                </Panel>
              );
            })}
          </div>
        </>
      )}

      <div className="eyebrow" style={{ margin: "6px 0 10px" }}>Operating normally · {healthy.length}</div>
      <Panel style={{ marginBottom: 22 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))" }}>
          {healthy.map((s) => (
            <div key={s.name} style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderBottom: "1px solid var(--ga-line)" }}>
              <StatusChip status="OK" />
              <span style={{ fontWeight: 500 }}>{s.name}</span>
            </div>
          ))}
          {healthy.length === 0 && <div className="sub" style={{ padding: 16 }}>No service is reported as operating normally.</div>}
        </div>
      </Panel>

      <AhaHealthPanel aha={health?.aha} onRefreshed={(aha) => setHealth((current) => ({ ...current, aha }))} />

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(420px,100%),1fr))", gap: 16, alignItems: "start" }}>
        <Panel title="Training centers" subtitle="Users and error rate per center">
          {(health?.tenantHealth || []).length === 0 ? (
            <StatePanel kind="empty" what="training center health" why="No training center has reported activity yet." />
          ) : (
            <div className="tw">
              <table className="tbl" style={{ minWidth: 0 }}>
                <thead><tr><th>Center</th><th className="r">Users</th><th className="r">Errors</th><th>Status</th></tr></thead>
                <tbody>
                  {health.tenantHealth.map((t) => {
                    const warn = t.status === "warning";
                    return (
                      <tr key={t.id}>
                        <td><b style={{ fontWeight: 600 }}>{t.name}</b><div className="sub mono" style={{ fontSize: 12 }}>{t.code}</div></td>
                        <td className="r mono">{t.users}</td>
                        <td className="r mono" style={{ color: warn ? "var(--st-warn)" : undefined, fontWeight: warn ? 700 : 500 }}>{t.errorPct}</td>
                        <td><StatusChip status={warn ? "Warn" : "OK"} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <Panel title="Recent issues" subtitle="Failed or warning events in the last 7 days">
          {(health?.recentIncidents || []).length === 0 ? (
            <StatePanel kind="empty" what="issues" why="No failed or warning events in the last 7 days. All systems operating normally." />
          ) : (
            <div>
              {health.recentIncidents.map((inc, i) => (
                <div key={inc.id} style={{ padding: "14px 18px", borderTop: i ? "1px solid var(--ga-line)" : 0 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <b style={{ fontWeight: 600, fontSize: 14 }}>{inc.description}</b>
                    <span className="sub mono" style={{ fontSize: 12, whiteSpace: "nowrap" }}>{inc.tsAgo}</span>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6, flexWrap: "wrap" }}>
                    <StatusChip status={inc.status && inc.status.toLowerCase().startsWith("fail") ? "Failed" : "Warn"} label={inc.status} />
                    {inc.tenantCode && <span className="sub mono" style={{ fontSize: 12 }}>{inc.tenantCode}</span>}
                    {inc.actor?.name && <span className="sub" style={{ fontSize: 12 }}>· {inc.actor.name}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>
    </>
  );
}
